import { type RankingItem } from '../types';
import { ArrowLeft } from 'lucide-react';
import { CriterionScale } from '../../../components/prioritization/tools/CriteriaScales';
import type { CriterionValueDisplay } from '../../../components/prioritization/tools/useItemCriteriaValues';

interface ComparisonViewProps {
  item1: RankingItem;
  item2: RankingItem;
  onSelect: (winnerId: string) => void;
  onUndo: () => void;
  progress: number;
  totalComparisons: number;
  currentComparison: number;
  canUndo: boolean;
  /** Optional per-item criteria values — shown as filled dot scales below the item name */
  item1CriteriaValues?: CriterionValueDisplay[];
  item2CriteriaValues?: CriterionValueDisplay[];
}

export function ComparisonView({
  item1,
  item2,
  onSelect,
  onUndo,
  progress,
  totalComparisons,
  currentComparison,
  canUndo,
  item1CriteriaValues,
  item2CriteriaValues,
}: ComparisonViewProps) {
  // Keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === '1' || e.key === 'ArrowLeft') {
      e.preventDefault();
      onSelect(item1.id);
    } else if (e.key === '2' || e.key === 'ArrowRight') {
      e.preventDefault();
      onSelect(item2.id);
    } else if (e.key === 'Backspace' && canUndo) {
      e.preventDefault();
      onUndo();
    }
  };

  return (
    <div className="max-w-4xl mx-auto" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg font-semibold text-[var(--text-pri)]">
            Comparison {currentComparison} of {totalComparisons}
          </span>
          <span className="text-sm font-medium text-[var(--text-sec)]">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
          <div
            className="bg-[var(--slate)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Prompt */}
      <div className="text-center mb-8">
        <p className="text-xl font-medium text-[var(--text-pri)] mb-2">
          Which is more important <em>right now</em>?
        </p>
        <p className="text-sm text-[var(--text-ter)]">
          Press 1 or 2, or click to select
        </p>
      </div>

      {/* Comparison Cards */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => onSelect(item1.id)}
            className="group relative p-8 bg-[var(--bg-raised)] shadow-md border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:shadow-lg transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="absolute top-4 left-4 text-2xl font-bold text-[var(--text-ter)] group-hover:text-[var(--text-ter)]">
              1
            </div>
            <h3 className="text-2xl font-bold text-[var(--text-pri)] mt-2">{item1.name}</h3>
            {item1CriteriaValues && item1CriteriaValues.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--rule)] space-y-2.5">
                {item1CriteriaValues.map((cv) => (
                  <CriterionScale key={cv.id} {...cv} />
                ))}
              </div>
            )}
          </button>

          <button
            onClick={() => onSelect(item2.id)}
            className="group relative p-8 bg-[var(--bg-raised)] shadow-md border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:shadow-lg transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="absolute top-4 left-4 text-2xl font-bold text-[var(--text-ter)] group-hover:text-[var(--text-ter)]">
              2
            </div>
            <h3 className="text-2xl font-bold text-[var(--text-pri)] mt-2">{item2.name}</h3>
            {item2CriteriaValues && item2CriteriaValues.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--rule)] space-y-2.5">
                {item2CriteriaValues.map((cv) => (
                  <CriterionScale key={cv.id} {...cv} />
                ))}
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Undo Button */}
      {canUndo && (
        <div className="text-center">
          <button
            onClick={onUndo}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Undo last comparison
          </button>
        </div>
      )}
    </div>
  );
}
